/**
 * MobileBottomNav Component
 *
 * Menu de navegação inferior para dispositivos mobile e tablet.
 * Usa a mesma configuração de rotas e permissões do Sidebar (erpNavigation).
 */

import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, LayersPlus, UserRound, X } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import {
  ERP_MENU_ITEMS,
  ERP_MOBILE_EXTRA_ITEMS,
  ERP_MOBILE_MAIN_BAR_IDS,
  filterErpMenuItems,
  isErpParentItemActive,
  isErpSubmenuItemActive,
  type ErpMenuItem,
} from '../../config/erpNavigation';
import './MobileBottomNav.css';

interface SubMenuItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ size?: number }>;
  desc?: string;
}

interface MainNavItem {
  id: string;
  name: string;
  icon: React.ComponentType<{ size?: number }>;
  path?: string;
  children?: SubMenuItem[];
}

const MAIN_BAR_BUTTONS = 6;

function toMobileNavItem(item: ErpMenuItem): MainNavItem {
  return {
    id: item.id,
    name: item.label,
    icon: item.icon,
    path: item.path,
    children: item.submenu?.map((sub) => ({
      name: sub.label,
      path: sub.path,
      icon: sub.icon ?? FileText,
    })),
  };
}

const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, hasModuleAccess, isAdmin, loading: permissionsLoading } = usePermissions();
  const [activeSubMenu, setActiveSubMenu] = useState<MainNavItem | null>(null);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const routeAccessCtx = useMemo(
    () => ({
      isAdmin,
      permissionsLoading,
      hasPermission,
      hasModuleAccess,
    }),
    [isAdmin, permissionsLoading, hasPermission, hasModuleAccess]
  );

  const filteredMenuItems = useMemo(
    () => filterErpMenuItems([...ERP_MENU_ITEMS, ...ERP_MOBILE_EXTRA_ITEMS], routeAccessCtx),
    [routeAccessCtx]
  );

  const { mainItems, moreItems } = useMemo(() => {
    const preferred = ERP_MOBILE_MAIN_BAR_IDS
      .map((id) => filteredMenuItems.find((item) => item.id === id))
      .filter((item): item is ErpMenuItem => !!item);
    const preferredIds = new Set(preferred.map((item) => item.id));
    const reserved = 2;
    const slots = Math.max(preferred.length, MAIN_BAR_BUTTONS - reserved);
    const fillers = filteredMenuItems.filter((item) => !preferredIds.has(item.id));
    const chosen = [...preferred, ...fillers].slice(0, slots);
    const chosenIds = new Set(chosen.map((item) => item.id));
    return {
      mainItems: chosen.map(toMobileNavItem),
      moreItems: filteredMenuItems
        .filter((item) => !chosenIds.has(item.id))
        .map(toMobileNavItem),
    };
  }, [filteredMenuItems]);

  const isActive = (path?: string) =>
    path ? location.pathname === path || location.pathname.startsWith(`${path}/`) : false;

  const getMenuItemById = (id: string) => filteredMenuItems.find((item) => item.id === id);

  const isMainItemActive = (item: MainNavItem) => {
    const menuItem = getMenuItemById(item.id);
    if (menuItem?.submenu?.length) {
      return (
        isErpParentItemActive(location.pathname, menuItem) ||
        menuItem.submenu.some((sub) =>
          isErpSubmenuItemActive(location.pathname, menuItem.submenu!, sub.path)
        )
      );
    }
    return isActive(item.path);
  };

  const isDrawerChildActive = (parentId: string | undefined, path: string) => {
    if (!parentId) return isActive(path);
    const menuItem = getMenuItemById(parentId);
    if (menuItem?.submenu?.length) {
      return isErpSubmenuItemActive(location.pathname, menuItem.submenu, path);
    }
    return isActive(path);
  };

  const handleMainItemClick = (item: MainNavItem) => {
    if (item.children?.length) {
      setActiveSubMenu(item);
      setIsMoreMenuOpen(false);
    } else if (item.path) {
      navigate(item.path);
      setActiveSubMenu(null);
    }
  };

  const handleDrawerItemClick = (item: MainNavItem | SubMenuItem) => {
    const navItem = item as MainNavItem;
    if ('children' in navItem && navItem.children?.length) {
      setActiveSubMenu(navItem);
      return;
    }
    if ('path' in item && item.path) {
      navigate(item.path);
      setActiveSubMenu(null);
      setIsMoreMenuOpen(false);
    }
  };

  const closeDrawer = () => {
    setActiveSubMenu(null);
    setIsMoreMenuOpen(false);
  };

  const drawerItems: (MainNavItem | SubMenuItem)[] = activeSubMenu?.children?.length
    ? activeSubMenu.children
    : moreItems;

  const profileActive = location.pathname === '/erp/perfil' || location.pathname.startsWith('/erp/perfil/');

  return (
    <>
      <nav className="mobile-bottom-nav">
        {mainItems.map((item) => {
          const active =
            isMainItemActive(item) ||
            activeSubMenu?.id === item.id;

          return (
            <button
              key={item.id}
              onClick={() => handleMainItemClick(item)}
              className={`mobile-nav-button ${active ? 'active' : ''}`}
            >
              <item.icon size={22} strokeWidth={active ? 2.5 : 2} />
              <span className="mobile-nav-label">{item.name}</span>
              {active && <div className="mobile-nav-indicator" />}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => {
            navigate('/erp/perfil');
            closeDrawer();
          }}
          className={`mobile-nav-button ${profileActive ? 'active' : ''}`}
        >
          <UserRound size={22} strokeWidth={profileActive ? 2.5 : 2} />
          <span className="mobile-nav-label">Perfil</span>
          {profileActive && <div className="mobile-nav-indicator" />}
        </button>

        <button
          type="button"
          onClick={() => {
            setIsMoreMenuOpen(true);
            setActiveSubMenu(null);
          }}
          className={`mobile-nav-button ${isMoreMenuOpen ? 'active' : ''}`}
        >
          <LayersPlus size={22} strokeWidth={isMoreMenuOpen ? 2.5 : 2} />
          <span className="mobile-nav-label">Mais</span>
        </button>
      </nav>

      {(activeSubMenu || isMoreMenuOpen) && (
        <div className="mobile-nav-drawer">
          <div className="mobile-nav-overlay" onClick={closeDrawer} />
          <div className="mobile-nav-drawer-content">
            <div className="mobile-nav-drawer-header">
              <div className="mobile-nav-drawer-header-left">
                <div className="mobile-nav-drawer-icon">
                  {activeSubMenu ? <activeSubMenu.icon size={24} /> : <LayersPlus size={24} />}
                </div>
                <div>
                  <h2 className="mobile-nav-drawer-title">
                    {activeSubMenu ? activeSubMenu.name : 'Menu'}{' '}
                    <span className="mobile-nav-drawer-title-accent">
                      {activeSubMenu ? 'Módulo' : 'Completo'}
                    </span>
                  </h2>
                  <p className="mobile-nav-drawer-subtitle">
                    {activeSubMenu
                      ? `Selecione uma opção de ${activeSubMenu.name}`
                      : 'Todos os módulos do ERP'}
                  </p>
                </div>
              </div>
              <button onClick={closeDrawer} className="mobile-nav-drawer-close">
                <X size={24} />
              </button>
            </div>

            {activeSubMenu && (
              <button
                type="button"
                className="mobile-nav-drawer-back"
                onClick={() => setActiveSubMenu(null)}
              >
                ← Voltar aos módulos
              </button>
            )}

            <div className="mobile-nav-drawer-grid">
              {drawerItems.length === 0 && (
                <p className="mobile-nav-drawer-empty">Nenhum outro módulo disponível.</p>
              )}
              {drawerItems.map((item) => {
                const isModule = 'children' in item && !!(item as MainNavItem).children?.length;
                const path = 'path' in item ? item.path : undefined;
                const Icon = item.icon;
                const name = 'name' in item ? item.name : '';
                const active = isDrawerChildActive(activeSubMenu?.id, path || '');

                return (
                  <button
                    key={path || name}
                    onClick={() => handleDrawerItemClick(item as MainNavItem)}
                    className={`mobile-nav-drawer-item ${active ? 'active' : ''}`}
                  >
                    <div className={`mobile-nav-drawer-item-icon ${active ? 'active' : ''}`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <span className="mobile-nav-drawer-item-name">{name}</span>
                      {isModule && (
                        <span className="mobile-nav-drawer-item-desc">
                          {((item as MainNavItem).children?.length ?? 0)} opções
                        </span>
                      )}
                      {'desc' in item && item.desc && (
                        <span className={`mobile-nav-drawer-item-desc ${active ? 'active' : ''}`}>
                          {item.desc}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mobile-nav-drawer-spacer" />
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav;
